/**
 * Loader des details riches de controles (CIS/ISO/CyFun/NIS2 paraphrase
 * + remediation etape-par-etape).
 *
 * Fallback chain : lang demande -> _fallback declare -> en -> fr-BE -> null
 * Si un controle n'a pas de detail (pilote 5 controles), getControlDetail
 * retourne null et le composant UI peut afficher un fallback "Detail a venir".
 */
import type { ControlDetail } from '../../domain/types';

import frBE from './fr-BE.json';
import en   from './en.json';
import nlBE from './nl-BE.json';
import deBE from './de-BE.json';

type Lang = 'fr-BE' | 'en' | 'nl-BE' | 'de-BE';

const SOURCES: Record<Lang, Record<string, ControlDetail | unknown>> = {
  'fr-BE': frBE as Record<string, ControlDetail | unknown>,
  'en':    en   as Record<string, ControlDetail | unknown>,
  'nl-BE': nlBE as Record<string, ControlDetail | unknown>,
  'de-BE': deBE as Record<string, ControlDetail | unknown>,
};

const FALLBACK_ORDER: Lang[] = ['en', 'fr-BE'];

/**
 * Recupere le detail d un controle pour une langue donnee.
 * Suit le fallback chain. Retourne null si aucun detail trouve.
 */
export function getControlDetail(controlId: string, lang: Lang): ControlDetail | null {
  const tried = new Set<Lang>();

  const tryLang = (l: Lang): ControlDetail | null => {
    if (tried.has(l)) return null;
    tried.add(l);

    const source = SOURCES[l];
    if (!source) return null;

    // _fallback redirect (pour les locales sans contenu propre)
    const fb = (source as Record<string, unknown>)._fallback;
    if (typeof fb === 'string') {
      return tryLang(fb as Lang);
    }

    const entry = source[controlId];
    if (entry && typeof entry === 'object' && 'frameworks' in entry) {
      return entry as ControlDetail;
    }
    return null;
  };

  // 1. Langue demandee
  const direct = tryLang(lang);
  if (direct) return direct;

  // 2. Fallback chain
  for (const fb of FALLBACK_ORDER) {
    const result = tryLang(fb);
    if (result) return result;
  }

  return null;
}

/** True si le controle a un detail riche disponible (peu importe la langue). */
export function hasControlDetail(controlId: string): boolean {
  for (const lang of Object.keys(SOURCES) as Lang[]) {
    const src = SOURCES[lang];
    if (src._fallback) continue;
    if (controlId in src) return true;
  }
  return false;
}

/** Compteur : combien de controles ont un detail riche (pour debug/admin). */
export function getControlsWithDetailCount(): number {
  const ids = new Set<string>();
  for (const lang of Object.keys(SOURCES) as Lang[]) {
    const src = SOURCES[lang];
    if (src._fallback) continue;
    Object.keys(src).filter((k) => !k.startsWith('_')).forEach((k) => ids.add(k));
  }
  return ids.size;
}
